import classNames from "classnames";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {EmptyTable} from "@/components/table/subComponents/EmptyTable/EmptyTable.tsx";
import {Table} from "@/components/table/Table.tsx";
import {ColumnSpec, Sorting, SortingDirection} from "@/components/table/Table.types.ts";
import {Tooltip} from "@/components/toolTip/Tooltip.tsx";
import {MaintenanceSchedule} from "@/lib/types/maintenance.ts";
import {DisconnectScheduleAction} from "@/pages/checks/update/sections/maintenance/actions/DisconnectScheduleAction.tsx";
import {LinkScheduleAction} from "@/pages/checks/update/sections/maintenance/actions/LinkScheduleAction.tsx";
import {MaintenanceSchedulesStatePill} from "@/pages/maintenance/maintenance-dashboard/tabs/schedules/MaintenanceSchedulesTable/components/MaintenanceSchedulesStatePill.tsx";
import {TimeUntil} from "@/pages/maintenance/maintenance-dashboard/tabs/schedules/MaintenanceSchedulesTable/components/TimeUntil.tsx";
import {getDurationDisplay} from "@/pages/maintenance/maintenance-dashboard/tabs/schedules/MaintenanceSchedulesTable/MaintenanceSchedulesTable.utils.ts";
import {getRecurrenceDisplay} from "@/pages/maintenance/maintenance.utils.ts";

import styles from "./CheckLinkedSchedulesTable.module.scss";

interface CheckLinkedSchedulesTableProps {
  items: MaintenanceSchedule[];
  sortColumn: string;
  sortDirection: SortingDirection;
  onSortingChange?: (sorting: Sorting) => void;
  loading?: boolean;
}

export const CheckLinkedSchedulesTable = ({
  items,
  sortColumn,
  sortDirection,
  onSortingChange,
  loading,
}: CheckLinkedSchedulesTableProps) => {
  const {user} = useCurrentUserContext();

  const specs: ColumnSpec<MaintenanceSchedule>[] = [
    {
      fieldName: "name",
      fieldTitle: "Name",
      enableSorting: true,
      meta: {
        headerClassName: styles.nameColumn,
      },
      accessor: (schedule: MaintenanceSchedule) => (
        <Tooltip tooltipContent={schedule.name} delayMs={500}>
          <div className="ellipsis-overflow pulseTextBodySmall pulse-color-gray-900">{schedule.name}</div>
        </Tooltip>
      ),
    },
    {
      fieldName: "status",
      fieldTitle: "Status",
      enableSorting: true,
      meta: {
        headerClassName: classNames("text-nowrap", styles.statusColumn),
      },
      accessor: (schedule: MaintenanceSchedule) => <MaintenanceSchedulesStatePill schedule={schedule} />,
    },
    {
      fieldName: "next_occurrence",
      fieldTitle: "Next Scheduled",
      enableSorting: true,
      meta: {
        headerClassName: classNames("text-nowrap", styles.nextScheduledColumn),
      },
      accessor: (schedule: MaintenanceSchedule) => <TimeUntil schedule={schedule} user={user!} />,
    },
    {
      fieldName: "type",
      fieldTitle: "Repeat",
      enableSorting: true,
      meta: {
        headerClassName: classNames(styles.repeatColumn),
      },
      accessor: (schedule: MaintenanceSchedule) => {
        const recurrenceDisplay = getRecurrenceDisplay(schedule);
        return (
          <Tooltip tooltipContent={recurrenceDisplay} delayMs={500}>
            <div className="ellipsis-overflow pulseTextBodySmall pulse-color-gray-900">{recurrenceDisplay}</div>
          </Tooltip>
        );
      },
    },
    {
      fieldName: "duration_minutes",
      fieldTitle: "Duration",
      enableSorting: true,
      meta: {
        headerClassName: classNames("text-nowrap", styles.durationColumn),
      },
      accessor: (schedule: MaintenanceSchedule) => (
        <span className="pulseTextBodySmall">{getDurationDisplay(schedule.duration_minutes)}</span>
      ),
    },
    ...(user?.has_write_access
      ? [
          {
            fieldName: "disconnect",
            meta: {
              columnClassName: classNames(styles.actionColumn),
              headerClassName: styles.actionColumn,
            },
            accessor: (schedule: MaintenanceSchedule) => <DisconnectScheduleAction schedule={schedule} />,
          },
        ]
      : []),
  ];

  return (
    <>
      <Table
        rows={items || []}
        specs={specs}
        sorting={{column: sortColumn, direction: sortDirection}}
        onSortingChange={onSortingChange}
        isLoading={loading}
        noDataComponent={
          <EmptyTable colSpan={specs.length}>
            <div className="d-flex flex-column align-items-center gap-3 py-5">
              <div className="pulse-color-gray-800 pulseTextBodyBold">
                No Maintenance Schedule linked to this Check.
              </div>
              <LinkScheduleAction />
            </div>
          </EmptyTable>
        }
        fixedWidth={true}
        minWidth={"1150px"}
      />
    </>
  );
};
